import type { ComponentType } from "react";
import { NavLink, useLocation } from "react-router-dom";

import { Tooltip } from "@/components/ui/tooltip";
import {
  ArrowLeft,
  BellRinging,
  CalendarDots,
  ChatCircle,
  FolderOpen,
  GearSix,
  GlobeHemisphereWest,
  PuzzlePiece,
  Scales,
  ShieldStar,
  Target,
  Toolbox,
} from "@phosphor-icons/react";

import { cn } from "@/lib/utils";

// A nav/section icon: any component that takes a className (Phosphor + lucide
// both qualify). Phosphor icons render at the duotone weight set in main.tsx.
export type IconType = ComponentType<{ className?: string }>;

// The agent's customization surfaces, grouped for the settings overlay + ⌘K.
// Chat is not a section — it's the page itself. Shared by the in-chat Settings
// overlay (ProjectDetail) AND the create/edit form routes, so the left nav is
// identical whether you're managing a section or filling in a "new X" form.
export type SectionId = "tools" | "builtin" | "workspace" | "policy" | "channels" | "access" | "general" | "domains" | "scheduled" | "missions";

export type SectionDef = { id: SectionId; label: string; desc: string; icon: IconType };
export type SectionGroup = { group: string; items: SectionDef[] };

export const SECTION_GROUPS: SectionGroup[] = [
  {
    group: "Capabilities",
    items: [
      { id: "tools", label: "Integrations", desc: "Connect servers and choose the tools this agent exposes", icon: PuzzlePiece },
      { id: "builtin", label: "Built-in tools", desc: "Toggle the built-in /mcp tools (code, files, ask a human…)", icon: Toolbox },
      { id: "workspace", label: "Workspace", desc: "Browse and edit the agent's playbook, skills and state", icon: FolderOpen },
    ],
  },
  {
    group: "Governance",
    items: [
      { id: "policy", label: "Policy", desc: "Authorize, hold or deny tool calls; approvals", icon: Scales },
      { id: "channels", label: "Channels", desc: "Telegram & Slack channels for chat, approvals & the pending queue", icon: BellRinging },
      { id: "access", label: "Access", desc: "Members, roles and access tokens for this agent", icon: ShieldStar },
    ],
  },
  {
    group: "Agent",
    items: [
      { id: "general", label: "General", desc: "Endpoint, identity and deployment details", icon: GearSix },
      { id: "missions", label: "Missions", desc: "Long-running objectives the agent plans and executes autonomously", icon: Target },
      { id: "scheduled", label: "Scheduled", desc: "Recurring and one-off runs; pause, resume, skip or edit", icon: CalendarDots },
      { id: "domains", label: "Custom domains", desc: "Serve /mcp on your own hostname over HTTPS", icon: GlobeHemisphereWest },
    ],
  },
];

export const ALL_SECTIONS: SectionDef[] = SECTION_GROUPS.flatMap((g) => g.items);

// The deep-link hash maps to a settings section (so #policy etc. open the right
// place); anything else just opens the chat.
export function sectionFromHash(): SectionId | null {
  const h = window.location.hash.replace(/^#/, "");
  return ALL_SECTIONS.some((s) => s.id === h) ? (h as SectionId) : null;
}

// AgentNav is the agent-context sidebar rendered inside the standard app shell
// (Layout) whenever you're inside an agent: a "Back to dashboard" escape, a
// highlighted "Chat" link back to the conversation, then the grouped settings
// sections as routed links (/agents/:id/settings/:section). The shell's header
// (logo + workspace) and footer (user menu) stay identical to the main dashboard
// — only this middle nav swaps in.
export function AgentNav({
  projectId,
  onNavigate,
  collapsed,
}: {
  projectId: string;
  onNavigate?: () => void;
  collapsed?: boolean;
}) {
  const { pathname } = useLocation();
  const base = `/agents/${projectId}`;
  const itemCls = (active: boolean) =>
    cn(
      "flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
      collapsed && "lg:justify-center lg:gap-0 lg:px-2",
      active ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-accent hover:text-foreground",
    );
  // A section is active on its settings route — and, for the sections that own a
  // create/edit form route, on those sub-routes too (e.g. /policy/rules/new).
  const sectionActive = (id: SectionId) => {
    if (pathname === `${base}/settings/${id}`) return true;
    if (id === "policy") return pathname.startsWith(`${base}/policy/`);
    if (id === "channels") return pathname.startsWith(`${base}/channels/`);
    if (id === "access") return pathname.startsWith(`${base}/access-tokens/`);
    return false;
  };

  return (
    <nav className="flex flex-1 flex-col overflow-y-auto px-3 py-2">
      <Tooltip label="Back to dashboard" disabled={!collapsed}>
        <NavLink
          to="/agents"
          end
          onClick={onNavigate}
          className={cn(
            "mb-1 flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground",
            collapsed && "lg:justify-center lg:gap-0 lg:px-2",
          )}
        >
          <ArrowLeft className="h-4 w-4 shrink-0" />{" "}
          <span className={cn(collapsed && "lg:hidden")}>Back to dashboard</span>
        </NavLink>
      </Tooltip>
      <Tooltip label="Chat" disabled={!collapsed}>
        <NavLink
          to={base}
          end
          onClick={onNavigate}
          aria-current={pathname === base ? "page" : undefined}
          className={itemCls(pathname === base)}
        >
          <ChatCircle className="h-4 w-4 shrink-0" /> <span className={cn(collapsed && "lg:hidden")}>Chat</span>
        </NavLink>
      </Tooltip>

      <div className="my-2 border-t" />

      {SECTION_GROUPS.map((g) => (
        <div key={g.group} className="mb-3">
          <div
            className={cn(
              "px-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground",
              collapsed && "lg:hidden",
            )}
          >
            {g.group}
          </div>
          {g.items.map((it) => {
            const Icon = it.icon;
            return (
              <Tooltip key={it.id} label={it.label} disabled={!collapsed}>
                <NavLink
                  to={`${base}/settings/${it.id}`}
                  onClick={onNavigate}
                  aria-current={sectionActive(it.id) ? "page" : undefined}
                  className={itemCls(sectionActive(it.id))}
                >
                  <Icon className="h-4 w-4 shrink-0" />
                  <span className={cn("truncate", collapsed && "lg:hidden")}>{it.label}</span>
                </NavLink>
              </Tooltip>
            );
          })}
        </div>
      ))}
    </nav>
  );
}
